import React, { ReactNode, startTransition, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Filter, OptionObject, Select, SeverityItem } from '@fabric-ui/components';
import { Box } from '@mui/material';
import { getFieldTypeInfo } from '../../../utils/filterUtils';
import { IconVariant, ItemWithLogo } from '../../ItemWithLogo';
import { useExpressionBuilder } from '../ExpressionBuilderContext';
import { FilterOption } from '../types';

const MAX_DISPLAYED_OPTIONS = 300;

interface FilterByListProps {
  label: ReactNode;
  isLoadingOptions: boolean;
  options: FilterOption[];
  value: unknown[];
  onChange: (field: string, val: unknown) => void;
  field: string;
  type: string;
  onFilterChange: ((filter: Filter) => void) | undefined;
  totalOptionsCount: number;
  isSchemaField: boolean;
  setSearch: (search: string) => void;
  open?: boolean;
}

export const FilterByList: React.FC<FilterByListProps> = ({
  label,
  isLoadingOptions,
  options,
  value,
  onChange,
  field,
  type,
  onFilterChange,
  totalOptionsCount,
  isSchemaField,
  setSearch,
  open = false,
}) => {
  const { excludeNullFromList } = useExpressionBuilder();

  const { isTextField } = getFieldTypeInfo(type);
  const isSeverity = field.split('.')[1] === 'severity';

  const [displayOptions, setDisplayOptions] = useState(options);
  const [localValue, setLocalValue] = useState(value);
  const optionsRef = useRef(options);
  const localValueRef = useRef(localValue);
  optionsRef.current = options;
  localValueRef.current = localValue;

  const optionsKey = options.map(o => (o.value === null ? 'null' : String(o.value))).join('\u0000');

  useEffect(() => {
    const valueKey = value.map(v => (v === null ? 'null' : String(v))).join('\u0000');
    const localKey = localValueRef.current.map(v => (v === null ? 'null' : String(v))).join('\u0000');
    if (valueKey !== localKey) {
      setLocalValue(value);
    }
  }, [value]);

  useEffect(() => {
    const currentOptions = optionsRef.current;

    if (!open) {
      setDisplayOptions(currentOptions);
      return;
    }

    const currentValue = localValueRef.current || [];
    const selectedSet = new Set(currentValue);
    const optionByValue = new Map(currentOptions.map(o => [o.value, o]));
    const selectedOptions: FilterOption[] = [];
    const seen = new Set();

    currentValue.forEach(selectedValue => {
      if (selectedValue === null && excludeNullFromList) {
        return;
      }
      if (seen.has(selectedValue)) {
        return;
      }
      seen.add(selectedValue);
      selectedOptions.push(
        optionByValue.get(selectedValue) ?? {
          value: selectedValue,
          title: selectedValue === null ? 'Empty' : String(selectedValue),
        }
      );
    });

    const otherOptions = currentOptions.filter(o => !selectedSet.has(o.value));
    const orderedOptions = [...selectedOptions, ...otherOptions];
    const shouldSlice = isSchemaField && orderedOptions.length > MAX_DISPLAYED_OPTIONS && selectedOptions.length <= MAX_DISPLAYED_OPTIONS;
    setDisplayOptions(shouldSlice ? orderedOptions.slice(0, MAX_DISPLAYED_OPTIONS) : orderedOptions);
  }, [open, optionsKey, excludeNullFromList, isSchemaField]);

  const handleChange = useCallback(
    (val: unknown[]) => {
      setLocalValue(val);
      startTransition(() => {
        (onChange as (field: string | string[], val: unknown) => void)([field], val);
        if (type === 'string') {
          const hasNull = val.includes(null);
          const nonNullValues = val.filter(v => v !== null) as string[];
          onFilterChange?.({
            expression: {
              fieldName: field,
              stringCondition:
                val.length > 0 ? { in: { values: nonNullValues }, ...(hasNull && { addNullFilter: true }) } : { contains: '' },
            },
          });
        }
      });
    },
    [field, onChange, onFilterChange, type, setLocalValue]
  );

  const renderOption = useMemo(() => {
    if (isSeverity) {
      return ({ value }: FilterOption) => <SeverityItem value={value as string} />;
    }
    if (field.endsWith('.source_names')) {
      return ({ value }: FilterOption) => (
        <ItemWithLogo variant={IconVariant.sourcesMapByName} type={value as string} textStyle={{ overflow: 'auto' }} />
      );
    }
    return undefined;
  }, [isSeverity, field]);

  const filterFunc = useCallback(
    (opts: FilterOption[], { inputValue }: { inputValue: string }) => {
      setSearch(inputValue);
      return opts;
    },
    [setSearch]
  );

  return (
    <Box className="mt-2 block px-2 pb-2">
      <Select
        showOnlyAutoComplete
        size="small"
        acceptNullValue={!excludeNullFromList}
        label={label as React.ReactNode}
        variant="filter"
        loading={isLoadingOptions}
        isMultiple
        showInput={isTextField}
        options={displayOptions as unknown as OptionObject<unknown>[]}
        value={localValue}
        onChange={handleChange}
        totalOptionsCount={totalOptionsCount}
        renderOption={renderOption as unknown as (option: OptionObject<unknown>) => ReactNode}
        filterFunc={
          isSchemaField
            ? (filterFunc as unknown as (options: OptionObject<unknown>[], state: { inputValue: string }) => OptionObject<unknown>[])
            : undefined
        }
      />
    </Box>
  );
};
